import React, { FC, useCallback, useEffect } from "react";
import { useStripePaymentReturn } from "hooks/useStripePaymentReturn";
import { isEmpty } from "lodash";
import queryString from "query-string";
import { useTranslation } from "react-i18next";
import { connect } from "react-redux";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import { toast } from "react-toastify";
import { compose } from "redux";
import injectReducer from "reduxFolder/injectReducer";
import { getProfile } from "reduxFolder/reducers/auth";
import booksReducer, { booksCompleteOverpay } from "reduxFolder/reducers/books";
import { createStructuredSelector } from "reselect";
import { panelSelectOpened } from "../../reduxFolder/selectors/rightPanelSelectors";
import {
  toastResponseError,
  toastResponseSuccess,
} from "../../utils/responseMessageHelper";
import BooksAddFundsBlock from "./components/BooksAddFunds/BooksAddFundsBlock";
import { StyledGrid, Wrapper } from "./styles/BookRecipients.style";

interface BooksAddFundsProps {
  isPanelOpened: boolean;
  booksCompleteOverpay: () => void;
  getProfile: () => Promise<void>;
}

const BooksAddFunds: FC<BooksAddFundsProps> = React.memo(
  ({ isPanelOpened, booksCompleteOverpay, getProfile }) => {
    const params = useParams();
    const location = useLocation();
    const navigate = useNavigate();
    const { t } = useTranslation("common");

    const handleStripeRechargeSuccess = useCallback(() => {
      void getProfile()
        .then(() => toastResponseSuccess(t("common.stripePaymentSucceeded")))
        .catch((error) => toastResponseError(error));
    }, [getProfile, t]);

    const paymentResult = params?.payment_result;
    useStripePaymentReturn({
      isStripeReturn: paymentResult === "stripe-return",
      target:
        paymentResult === "stripe-return" ? { flow: "recharge" } : undefined,
      search: location.search,
      onSuccess: handleStripeRechargeSuccess,
      onPending: () =>
        toast.info(t("common.stripeProcessing"), { icon: false }),
      onTerminalError: () =>
        toastResponseError(t("common.stripePaymentFailed")),
      onError: (error) => toastResponseError(error),
      onInvalidReturn: () =>
        toastResponseError(t("common.stripeInvalidReturn")),
      onReturnHandled: () => navigate("/books/add-funds", { replace: true }),
    });

    useEffect(() => {
      const query = queryString.parse(location.search);
      if (paymentResult === "success") {
        if (isEmpty(query)) {
          booksCompleteOverpay();
        }
        navigate("/books/add-funds", { replace: true });
      } else if (paymentResult === "cancel") {
        toastResponseError(t("error.paymentWasCanceled"));
        navigate("/books/add-funds", { replace: true });
      }
    }, []);

    return (
      <Wrapper>
        <StyledGrid fluid>
          <BooksAddFundsBlock isPanelOpened={isPanelOpened} />
        </StyledGrid>
      </Wrapper>
    );
  },
);

const withBooksReducer = injectReducer({
  key: "books",
  reducer: booksReducer,
});

const withConnect = connect(
  createStructuredSelector({
    isPanelOpened: panelSelectOpened,
  }),
  {
    booksCompleteOverpay,
    getProfile,
  },
);

const enhance = compose(withBooksReducer, withConnect);

export default enhance(BooksAddFunds);
